import React, { Suspense, useState } from "react";
import {
  Container,
  unstable_createMuiStrictModeTheme as createMuiTheme,
  CssBaseline,
  ThemeProvider,
  Button,
  SvgIcon,
} from "@material-ui/core";
import TopBar from "./components/TopBar";
import { HashRouter as Router, Route, Switch } from "react-router-dom";
import ProjectListPage from "./pages/ProjectListPage";
import ProjectPage, { projectPagePath } from "./pages/ProjectPage";
import InitPage, { initPath } from "./pages/InitPage";
import styles from "./App.module.css";
import EnvironmentPage, { environmentPagePath } from "./pages/EnvironmentPage";
import { authToken, login } from "./components/Authentication";
import DiggerLogo from "./components/DiggerLogo";
import APIErrorBoundary from "./components/APIErrorBoundary";
import Spinner from "./components/Spinner";
import ServicePage, { servicePagePath } from "./pages/ServicePage";
import NoItems from "./components/NoItems";
import { ReactComponent as GithubLogo } from "./components/github-logo.svg";
import DeploymentPage, { deploymentPagePath } from "./pages/DeploymentPage";
import { IntercomProvider, useIntercom } from "react-use-intercom";

const INTERCOM_APP_ID = "ceh8qhlc";

export default function App() {
  const [darkMode, setDarkMode] = useDarkMode();
  const theme = React.useMemo(
    () =>
      createMuiTheme({
        palette: {
          type: darkMode ? "dark" : "light",
        },
      }),
    [darkMode]
  );

  return (
    <ThemeProvider theme={theme}>
      <IntercomProvider appId={INTERCOM_APP_ID}>
        <Router>
          <CssBaseline />
          {authToken ? (
            <>
              <TopBar darkMode={darkMode} onDarkmodeChange={setDarkMode} />
              <Container maxWidth="lg" className={styles.container}>
                <APIErrorBoundary>
                  <Suspense fallback={<Spinner />}>
                    <Pages />
                  </Suspense>
                </APIErrorBoundary>
              </Container>
            </>
          ) : (
            <div className={styles.loginContainer}>
              <DiggerLogo className={styles.logo} />
              <Button
                variant="outlined"
                onClick={login}
                startIcon={
                  <SvgIcon>
                    <GithubLogo />
                  </SvgIcon>
                }
              >
                Login via Github
              </Button>
            </div>
          )}
        </Router>
      </IntercomProvider>
    </ThemeProvider>
  );
}

function Pages() {
  // boot up intercom
  const { boot } = useIntercom();
  boot();

  return (
    <Switch>
      <Route exact path="/" component={ProjectListPage} />
      <Route path={servicePagePath} component={ServicePage} />
      <Route path={environmentPagePath} component={EnvironmentPage} />
      <Route path={initPath} component={InitPage} />
      <Route path={deploymentPagePath} component={DeploymentPage} />
      <Route path={projectPagePath} component={ProjectPage} />
      <Route path="*">
        <NoItems>Page not found</NoItems>
      </Route>
    </Switch>
  );
}

function useDarkMode(): [boolean, (darkMode: boolean) => void] {
  const [darkMode, setDarkMode] = useState(() => {
    const remembered: boolean | null = JSON.parse(
      localStorage.getItem("darkMode") ?? "null"
    );
    return (
      remembered ?? window.matchMedia("(prefers-color-scheme: dark)").matches
    );
  });

  return [
    darkMode,
    (darkMode) => {
      localStorage.setItem("darkMode", JSON.stringify(darkMode));
      setDarkMode(darkMode);
    },
  ];
}
